import { apiGet, apiPost } from './client';

export type ReportJobStatus =
  | 'pending'
  | 'generating'
  | 'ready'
  | 'regenerating'
  | 'failed'
  // Merchant had no transactions for the period — no file generated (no download).
  | 'no_data';

export type ReportJobRow = {
  id: string;
  merchantId: string;
  merchantName: string | null;
  periodDateYmd: string; // YYYY-MM-DD GMT+7
  periodStart: string;
  periodEnd: string;
  fileVersion: number;
  status: ReportJobStatus;
  rowCount: number;
  cashinCount: number;
  cashoutCount: number;
  pendingCount: number;
  /** Headline amounts of the file — settlement reports only, else null. */
  totalGross: number | null;
  totalFee: number | null;
  totalNet: number | null;
  fileSizeBytes: number | null;
  dataHash: string | null;
  generatedAt: string;
  lastCheckedAt: string | null;
  supersedesVersionId: string | null;
  regeneratedReason: string | null;
  generatedBy: string;
  failureReason: string | null;
  isLatestVersion: boolean;
};

export type ReportJobDetail = ReportJobRow & {
  versionChain: Array<{
    id: string;
    fileVersion: number;
    status: ReportJobStatus;
    generatedAt: string;
    regeneratedReason: string | null;
    rowCount: number;
    pendingCount: number;
  }>;
};

export type ReportsListParams = {
  fromDate?: string;
  toDate?: string;
  merchantId?: string;
  cursor?: string;
  limit?: number;
  includeAllVersions?: boolean;
};

export type ReportsListResponse = {
  rows: ReportJobRow[];
  hasMore: boolean;
};

export type DownloadResponse = {
  url: string;
  filename: string;
  expiresInSeconds: number;
  fileSizeBytes: number | null;
};

/**
 * Both report kinds run on the same job pipeline and expose identical routes:
 *   transactions — deposits + disbursements CREATED in the period
 *   settlements  — deposits SETTLED in the period (the Wallet's settlement rows)
 */
export type ReportKind = 'transaction' | 'settlement';

function makeReportsApi(path: 'transactions' | 'settlements') {
  const base = `/api/reports/${path}`;
  return {
    list: (params: ReportsListParams = {}) => {
      const q = new URLSearchParams();
      if (params.fromDate) q.set('fromDate', params.fromDate);
      if (params.toDate) q.set('toDate', params.toDate);
      if (params.merchantId) q.set('merchantId', params.merchantId);
      if (params.cursor) q.set('cursor', params.cursor);
      if (params.limit) q.set('limit', String(params.limit));
      if (params.includeAllVersions)
        q.set('includeAllVersions', String(params.includeAllVersions));
      const qs = q.toString();
      return apiGet<ReportsListResponse>(`${base}${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => apiGet<ReportJobDetail>(`${base}/${id}`),
    download: (id: string) => apiGet<DownloadResponse>(`${base}/${id}/download`),
    regenerate: (input: { merchantId: string; periodDateYmd: string }) =>
      apiPost<{
        jobId: string;
        status: ReportJobStatus;
        fileVersion: number;
        message: string;
      }>(`${base}/regenerate`, input),
  };
}

export const reportsApi = makeReportsApi('transactions');
export const settlementReportsApi = makeReportsApi('settlements');

export const reportsApiByKind: Record<ReportKind, ReturnType<typeof makeReportsApi>> = {
  transaction: reportsApi,
  settlement: settlementReportsApi,
};
